import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../utils/axios";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../helpers";
import {
  Users,
  QrCode,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  Copy,
  Check,
  Download,
  Share2,
  RefreshCw,
  Loader2,
  ExternalLink,
} from "lucide-react";

const AgentDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchAgentStats();
    }
  }, [user]);

  const fetchAgentStats = async () => {
    setLoading(true);
    try {
      // 1. Get agent detail & financials
      const [detailRes, playersRes] = await Promise.all([
        api.get(`/user/${user?.id}/collaborators/detail`),
        api.get(`/agents/${user?.id}/users`),
      ]);

      if (detailRes.data?.data) {
        setStats(detailRes.data.data);
      }
      if (playersRes.data?.data?.players) {
        setPlayers(playersRes.data.data.players || []);
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to load agent statistics");
    } finally {
      setLoading(false);
    }
  };

  const qrCode = user?.qr_code || `AG_${user?.id}`;
  const referralLink = `https://plyzoo.com/referal/${user?.referalcode}?agent_code=${qrCode}&source=qr`;
  const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
    referralLink
  )}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral QR link copied!");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleDownloadQR = async () => {
    try {
      const response = await fetch(qrImageUrl);
      const blob = await response.blob();
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = URL.createObjectURL(blob);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const width = 400;
        const height = 480;

        canvas.width = width;
        canvas.height = height;

        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);

          ctx.fillStyle = "#059669";
          ctx.fillRect(0, 0, width, 60);

          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 20px Inter, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("PlyZoo Store / Agent QR", width / 2, 38);

          const qrSize = 280;
          ctx.drawImage(img, (width - qrSize) / 2, 80, qrSize, qrSize);

          ctx.fillStyle = "#111827";
          ctx.font = "bold 16px Inter, sans-serif";
          ctx.fillText(user?.name || "Agent Store", width / 2, 390);

          ctx.fillStyle = "#6B7280";
          ctx.font = "12px monospace";
          ctx.fillText(`Code: ${qrCode} • UID: #${user?.id}`, width / 2, 415);

          ctx.fillStyle = "#059669";
          ctx.font = "11px Inter, sans-serif";
          ctx.fillText("Scan with camera to register and play", width / 2, 445);

          canvas.toBlob((finalBlob) => {
            if (finalBlob) {
              const url = window.URL.createObjectURL(finalBlob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Agent_${user?.name}_QRCode.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              toast.success("QR Code downloaded successfully!");
            }
          });
        }
      };
    } catch (e) {
      toast.error("Failed to download QR image");
    }
  };

  const agentUser = stats?.user || {};
  const network = stats?.network_stats || {};

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white p-6 sm:p-8 rounded-3xl shadow-sm relative overflow-hidden">
        <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-xs font-semibold uppercase tracking-wider">
                Store / Agent Portal
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Welcome back, {user?.name || "Partner"}
            </h1>
            <p className="text-sm text-white/80 mt-1 max-w-xl">
              Track your assigned players, QR acquisitions, wallet balances, and performance commissions in real time.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchAgentStats}
              className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition text-white"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* QR Banner & Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* QR Card */}
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center text-center">
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
            <QrCode size={22} />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Your Store QR Code</h2>
          <p className="text-xs text-gray-400 mb-4">Players scan this to instantly bind to your agency</p>

          <div className="p-3 bg-white border border-gray-200 rounded-2xl shadow-sm mb-4">
            <img src={qrImageUrl} alt="Agent QR" className="w-48 h-48 rounded-xl object-contain" />
          </div>

          <div className="w-full space-y-2 mb-4">
            <div className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 py-1.5 px-3 rounded-xl border border-emerald-100">
              Code: {qrCode}
            </div>
          </div>

          <div className="flex gap-2 w-full">
            <button
              onClick={handleCopy}
              className="flex-1 py-2.5 px-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition"
            >
              {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
              <span>{copied ? "Copied Link" : "Copy Link"}</span>
            </button>
            <button
              onClick={handleDownloadQR}
              className="flex-1 py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition shadow-sm"
            >
              <Download size={14} />
              <span>Download QR</span>
            </button>
          </div>
        </div>

        {/* 4 Financial & Stats Cards */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Card 1: Wallet Balance */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Store Balance
              </span>
              <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl">
                <Wallet size={20} />
              </div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-gray-900">
                {formatCurrency(Number(agentUser.balance || 0))}
              </div>
              <span className="text-xs text-gray-400 mt-1 block">Current Available Credits</span>
            </div>
          </div>

          {/* Card 2: Total Assigned Players */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Assigned Players
              </span>
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl">
                <Users size={20} />
              </div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-gray-900">
                {players.length || network.total_referrals || 0}
              </div>
              <span className="text-xs text-gray-400 mt-1 block">Total Active Player Accounts</span>
            </div>
          </div>

          {/* Card 3: Total Deposits */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Total Recharges
              </span>
              <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl">
                <ArrowUpCircle size={20} />
              </div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-emerald-600">
                {formatCurrency(Number(agentUser.actual_total_deposit_amount || 0))}
              </div>
              <span className="text-xs text-gray-400 mt-1 block">Accumulated Deposit Volume</span>
            </div>
          </div>

          {/* Card 4: Total Commission Earned */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Earned Commission
              </span>
              <div className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl">
                <TrendingUp size={20} />
              </div>
            </div>
            <div>
              <div className="text-3xl font-extrabold text-gray-900">
                {formatCurrency(
                  Number(agentUser.total_bet_level_commission || 0) +
                  Number(agentUser.total_deposit_level_commission || 0)
                )}
              </div>
              <span className="text-xs text-gray-400 mt-1 block">Lifetime Partner Earnings</span>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Players List */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 space-y-4">
        <div className="flex justify-between items-center border-b pb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">My Players</h2>
            <p className="text-xs text-gray-400">Players registered under your store or QR code</p>
          </div>
          <span className="text-xs font-semibold px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
            {players.length} Players
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3.5">Player</th>
                <th className="px-5 py-3.5">Email</th>
                <th className="px-5 py-3.5">Registration Source</th>
                <th className="px-5 py-3.5">Registered Date</th>
                <th className="px-5 py-3.5 text-right">Deposits</th>
                <th className="px-5 py-3.5 text-right">Withdrawals</th>
                <th className="px-5 py-3.5 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-gray-400">
                    <Loader2 className="animate-spin inline-block mr-2" size={18} />
                    Loading player activity...
                  </td>
                </tr>
              ) : players.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-gray-400">
                    No players registered under this agent yet. Share your QR code to get started!
                  </td>
                </tr>
              ) : (
                players.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3.5">
                      <div className="font-semibold text-gray-800">{p.name}</div>
                      <span className="text-xs text-gray-400 font-mono">UID: #{p.id}</span>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs text-gray-600">{p.email}</td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${p.registration_source === "qr"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-blue-50 text-blue-700"
                          }`}
                      >
                        {p.registration_source === "qr" ? "QR Scan" : "Referral"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-500">
                      {new Date(p.created || Date.now()).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3.5 text-right font-medium text-emerald-600">
                      {formatCurrency(Number(p.actual_total_deposit_amount || 0))}
                    </td>
                    <td className="px-5 py-3.5 text-right font-medium text-rose-600">
                      {formatCurrency(Number(p.actual_total_withdrawal_amount || 0))}
                    </td>
                    <td className="px-5 py-3.5 text-right font-bold text-gray-800">
                      {formatCurrency(Number(p.balance || 0))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
